/** An upload with a time on it. `id` need only be unique within the set passed in. */
export interface DatedUpload {
    readonly id: string;
    /** ISO upload time. Absent on a file that has not landed yet. */
    readonly uploadedAt?: string;
}

/** Just the time, for callers that never need to tell two uploads apart. */
type TimedUpload = Pick<DatedUpload, 'uploadedAt'>;

interface UploadBatchProgress {
    /** Uploads in the current batch that have been answered for. */
    processedCount: number;
    /** Uploads in the current batch still waiting on an answer. */
    processingCount: number;
}

/**
 * How far apart two uploads can be and still read as one drop.
 *
 * Files are timestamped as each one lands, so this has to be wider than a large batch's transfer
 * or the earliest arrivals stop counting towards their own batch. It also has to be narrower than
 * someone coming back later to add one more file, who should be told "1 of 1".
 */
const BATCH_GAP_MS = 2 * 60 * 1000;

/** An upload's time in epoch milliseconds, or `undefined` where it has none we can read. */
function uploadedAtMs(upload: TimedUpload): number | undefined {
    if (!upload.uploadedAt) {
        return undefined;
    }
    const uploadedMs = new Date(upload.uploadedAt).getTime();
    return Number.isFinite(uploadedMs) ? uploadedMs : undefined;
}

/**
 * The oldest upload in a set, as epoch milliseconds, or `undefined` if none is dated.
 *
 * A batch's identity, for anything that has to outlive the component watching it. Oldest rather
 * than newest so the anchor holds still as the rest of the batch arrives, moving only once that
 * batch settles and leaves the set. Callers pass the uploads they consider in play; this filters
 * nothing itself.
 */
export default function earliestUploadedAtMs(uploads: readonly TimedUpload[]): number | undefined {
    return uploads.reduce<number | undefined>((earliest, upload) => {
        const uploadedMs = uploadedAtMs(upload);
        if (uploadedMs === undefined) {
            return earliest;
        }
        return earliest === undefined || uploadedMs < earliest ? uploadedMs : earliest;
    }, undefined);
}

/**
 * How far a batch now in flight has got — the numerator and denominator behind "3 of 8".
 *
 * The denominator is the problem this solves. "How many are still in flight" shrinks as answers
 * land, so quoting it makes the batch look like it is getting smaller instead of getting on. The
 * whole drop is needed, and it has to be recoverable from the uploads themselves, because the
 * component narrating it may unmount and lose any count it remembered.
 *
 * So a batch is defined by upload time: the run either side of the oldest unanswered upload,
 * joined while consecutive uploads are less than `BATCH_GAP_MS` apart. Anything answered for long
 * before this batch started falls outside the run and is not counted towards it.
 *
 * `awaiting` is passed in rather than derived here because "still waiting" is a judgement with a
 * clock in it, and the caller has usually already made it elsewhere. Two derivations of the same
 * thing is how a counter and a disabled button start disagreeing.
 */
export function uploadBatchProgress(
    uploads: readonly DatedUpload[],
    awaiting: readonly DatedUpload[]
): UploadBatchProgress {
    if (awaiting.length === 0) {
        return { processedCount: 0, processingCount: 0 };
    }

    const awaitingIds = new Set(awaiting.map((upload) => upload.id));
    const dated = uploads
        .flatMap((upload) => {
            const uploadedMs = uploadedAtMs(upload);
            return uploadedMs === undefined ? [] : [{ id: upload.id, uploadedMs }];
        })
        .sort((left, right) => left.uploadedMs - right.uploadedMs);

    // The oldest upload still waiting. Everything counted below is joined to it, so a batch that
    // has not started and one that finished an hour ago cannot pull each other's files in.
    const anchor = dated.findIndex((entry) => awaitingIds.has(entry.id));
    if (anchor === -1) {
        // Nothing in flight carries a timestamp, so there is no run to walk. Still honest about
        // how many are waiting; it just cannot say how many came before them.
        return { processedCount: 0, processingCount: awaiting.length };
    }

    let first = anchor;
    while (first > 0 && dated[first].uploadedMs - dated[first - 1].uploadedMs <= BATCH_GAP_MS) {
        first -= 1;
    }
    let last = anchor;
    while (last < dated.length - 1 && dated[last + 1].uploadedMs - dated[last].uploadedMs <= BATCH_GAP_MS) {
        last += 1;
    }

    // Both counts come off the same slice. Scoping only the numerator was a bug: a second batch
    // still in flight from ten minutes ago sits outside this run but was counted in the total, so
    // a two-file drop narrated itself as "1 of 5".
    const batch = dated.slice(first, last + 1);
    const processedCount = batch.filter((entry) => !awaitingIds.has(entry.id)).length;

    return { processedCount, processingCount: batch.length - processedCount };
}
